import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  filesWithoutUpload,
  isAbortError,
  keepAttachedGroups,
  uploadedUrls,
  type UploadGroup,
} from './mediaUploadGroups';

type SendFn = (input: string, mediaUrls?: string[]) => boolean | void | Promise<boolean | void>;

interface FileInputProps {
  ref: React.RefObject<HTMLInputElement | null>;
  type: 'file';
  accept: string;
  multiple: true;
  style: React.CSSProperties;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export interface MediaUploadHook {
  selectedFiles: File[];
  uploadingFiles: Set<File>;
  uploadProgress: number;
  isUploading: boolean;
  uploadError: string | null;
  /** Names of files dropped because they exceeded MAX_FILE_SIZE_BYTES. */
  rejectedFiles: string[];
  addFiles: (files: File[]) => void;
  handleAttachClick: () => void;
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleRemoveFile: (index: number) => void;
  handleSendWithMedia: (input: string) => Promise<void>;
  cancelUpload: () => void;
  /** Dismiss the size-error banner. */
  clearRejected: () => void;
  /** Dismiss the upload-error banner. */
  clearUploadError: () => void;
  fileInputProps: FileInputProps;
}

interface UploadState {
  files: File[];
  progress: number;
  uploading: boolean;
}

const INITIAL_STATE: UploadState = { files: [], progress: 0, uploading: false };

/** 30 MB in bytes — files larger than this are rejected before upload. */
export const MAX_FILE_SIZE_BYTES = 30 * 1024 * 1024;
export const MAX_MEDIA_FILES = 5;

/** Keep this list aligned with ChatMediaService.ALLOWED_TYPES. */
export const ACCEPTED_MEDIA_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
  'text/csv',
  'application/csv',
] as const;

const ACCEPTED_MEDIA_TYPE_SET = new Set<string>(ACCEPTED_MEDIA_TYPES);
const FILE_INPUT_ACCEPT = [...ACCEPTED_MEDIA_TYPES, '.docx', '.pptx', '.csv'].join(',');

export interface MediaFileSelection {
  files: File[];
  addedFiles: File[];
  oversizedFiles: string[];
  unsupportedFiles: string[];
  exceedsFileLimit: boolean;
}

export const selectMediaFiles = (
  existingFiles: File[],
  pickedFiles: File[],
): MediaFileSelection => {
  const oversizedFiles: string[] = [];
  const unsupportedFiles: string[] = [];
  const eligibleFiles: File[] = [];

  pickedFiles.forEach((file) => {
    if (!ACCEPTED_MEDIA_TYPE_SET.has(file.type)) {
      unsupportedFiles.push(file.name);
    } else if (file.size > MAX_FILE_SIZE_BYTES) {
      oversizedFiles.push(file.name);
    } else {
      eligibleFiles.push(file);
    }
  });

  const remainingSlots = Math.max(0, MAX_MEDIA_FILES - existingFiles.length);
  const addedFiles = eligibleFiles.slice(0, remainingSlots);

  return {
    files: [...existingFiles, ...addedFiles].slice(0, MAX_MEDIA_FILES),
    addedFiles,
    oversizedFiles,
    unsupportedFiles,
    exceedsFileLimit: eligibleFiles.length > remainingSlots,
  };
};

const selectionError = (selection: MediaFileSelection): string | null => {
  const messages: string[] = [];

  if (selection.unsupportedFiles.length > 0) {
    messages.push(`Unsupported attachment type: ${selection.unsupportedFiles.join(', ')}.`);
  }
  if (selection.exceedsFileLimit) {
    messages.push(`You can attach up to ${MAX_MEDIA_FILES} files.`);
  }

  return messages.length > 0 ? messages.join(' ') : null;
};

async function uploadFiles(
  files: File[],
  projectId: string,
  sessionId?: string | null,
  setProgress?: (p: number) => void,
  signal?: globalThis.AbortSignal,
): Promise<string[]> {
  const { chatService } = await import('../../services/chatService');

  return chatService.uploadMedia(files, projectId, sessionId, setProgress, signal);
}

// eslint-disable-next-line max-lines-per-function, max-statements -- One hook owns the tray, its uploads and send.
export const useMediaUpload = (
  projectId: string | null,
  onSend: SendFn,
  canUpload: boolean = true,
  sessionId?: string | null,
): MediaUploadHook => {
  const [state, setState] = useState<UploadState>(INITIAL_STATE);
  const [uploadingFiles, setUploadingFiles] = useState<Set<File>>(new Set());
  const [rejectedFiles, setRejectedFiles] = useState<string[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const filesRef = useRef<File[]>([]);
  const sendingRef = useRef(false);

  // Eager uploads for both landing and project pages: one group per pick, never the whole tray again.
  const groupsRef = useRef<UploadGroup[]>([]);

  const cancelEagerUpload = useCallback(() => {
    groupsRef.current.forEach((group) => group.controller.abort());
    groupsRef.current = [];
  }, []);

  const reset = useCallback(() => {
    cancelEagerUpload();
    filesRef.current = [];
    setUploadError(null);
    setUploadingFiles(new Set());
    setState(INITIAL_STATE);
  }, [cancelEagerUpload]);

  useEffect(() => () => { cancelEagerUpload(); }, [cancelEagerUpload]);

  const settleGroup = useCallback((group: UploadGroup) => {
    group.done = true;
    setState((s) => ({ ...s, uploading: groupsRef.current.some((g) => !g.done) }));
    setUploadingFiles((prev) => new Set(Array.from(prev).filter((file) => !group.files.includes(file))));
  }, []);

  /** Upload only ``files``; earlier groups keep their completed uploads. */
  const startEagerUpload = useCallback((files: File[]) => {
    const controller = new globalThis.AbortController();
    const promise = uploadFiles(
      files, projectId || 'temp', sessionId, (p) => setState((s) => ({ ...s, progress: p })), controller.signal,
    );
    const group: UploadGroup = { files, controller, promise, done: false };

    groupsRef.current = [...groupsRef.current, group];
    setState((s) => ({ ...s, uploading: true, progress: 0 }));
    void promise
      .then(() => { if (groupsRef.current.includes(group)) settleGroup(group); })
      .catch((err) => {
        if (!groupsRef.current.includes(group) || isAbortError(err)) return;
        groupsRef.current = groupsRef.current.filter((g) => g !== group);
        settleGroup(group);
        setUploadError('Attachment upload failed. Please remove the file or try again.');
      });
  }, [projectId, sessionId, settleGroup]);

  // eslint-disable-next-line max-statements
  const addFiles = useCallback((pickedFiles: File[]) => {
    if (pickedFiles.length === 0) return;

    const selection = selectMediaFiles(filesRef.current, pickedFiles);

    if (selection.oversizedFiles.length > 0) {
      setRejectedFiles((prev) => [...prev, ...selection.oversizedFiles]);
    }

    setUploadError(selectionError(selection));

    if (selection.addedFiles.length > 0) {
      filesRef.current = selection.files;
      setState((current) => ({ ...current, files: selection.files }));

      if (canUpload) {
        setUploadingFiles((prev) => {
          const next = new Set(prev);

          selection.addedFiles.forEach((file) => next.add(file));

          return next;
        });
        startEagerUpload(selection.addedFiles);
      }
    }
  }, [canUpload, startEagerUpload]);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(e.target.files || []));

    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [addFiles]);

  const handleRemoveFile = useCallback((i: number) => {
    setUploadError(null);
    const remaining = filesRef.current.filter((_, idx) => idx !== i);

    filesRef.current = remaining;
    // The other files keep their uploads; a pick with no file left is aborted.
    groupsRef.current = keepAttachedGroups(groupsRef.current, remaining);
    const uploading = groupsRef.current.some((group) => !group.done);

    setState((s) => ({ ...s, files: remaining, uploading, progress: uploading ? s.progress : 0 }));
    setUploadingFiles((prev) => new Set(Array.from(prev).filter((file) => remaining.includes(file))));
  }, []);

  // eslint-disable-next-line max-statements -- Keep the send guard, upload reuse and error states together.
  const handleSendWithMedia = useCallback(async (input: string) => {
    if (sendingRef.current) return;
    if (state.files.length === 0) {
      await onSend(input);

      return;
    }

    if (!canUpload) {
      await onSend(input);

      return;
    }

    sendingRef.current = true;
    setUploadError(null);
    setState((s) => ({ ...s, uploading: true }));
    setUploadingFiles(new Set(state.files));

    let uploaded = false;
    let groups: UploadGroup[] = [];
    const aborted = () => groups.some((group) => group.controller.signal.aborted);

    try {
      // Reuse every completed or running upload; upload only files without one (a failed pick).
      const missing = filesWithoutUpload(state.files, groupsRef.current);

      if (missing.length > 0) startEagerUpload(missing);
      groups = groupsRef.current;
      const urls = await uploadedUrls(state.files, groups);

      if (aborted()) return;

      uploaded = true;
      // Landing sends return false when project creation fails. Keep both the
      // selected files and completed upload so the same message can be retried.
      if (await onSend(input, urls) !== false) reset();
    } catch {
      if (aborted()) return;
      setUploadError(uploaded
        ? 'Message could not be sent. Your attachments are still selected. Please try again.'
        : 'Attachment upload failed. Please remove the file or try again.');
    } finally {
      sendingRef.current = false;
      setState((s) => ({ ...s, uploading: false, progress: 0 }));
      setUploadingFiles(new Set());
    }
  }, [state.files, canUpload, onSend, reset, startEagerUpload]);

  return {
    selectedFiles: state.files,
    uploadingFiles,
    uploadProgress: state.progress,
    isUploading: state.uploading,
    uploadError,
    rejectedFiles,
    addFiles,
    handleAttachClick: () => fileInputRef.current?.click(),
    handleFileChange,
    handleRemoveFile,
    handleSendWithMedia,
    cancelUpload: reset,
    clearRejected: () => setRejectedFiles([]),
    clearUploadError: () => setUploadError(null),
    fileInputProps: {
      ref: fileInputRef,
      type: 'file' as const,
      accept: FILE_INPUT_ACCEPT,
      multiple: true as const,
      style: { display: 'none' } as React.CSSProperties,
      onChange: handleFileChange,
    },
  };
};
